/**
 * Stream B — B69: Settings Team Management
 *
 * Load team members from Better Auth org API.
 * Invite flow via real apiFetch. No mocks.
 */
import { useState, useEffect, useCallback, type FormEvent } from "react";
import { formatDate } from "@/lib/i18n";
import {
  cancelOrganizationInvitation,
  inviteOrganizationMember,
  listOrganizationInvitations,
  listOrganizationMembers,
  removeOrganizationMember,
  type OrganizationInvitationRecord,
  type OrganizationMemberRecord,
} from "@/lib/organization-client";
import { Users, Mail, Shield, Trash2 } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

const ROLES = [
  { value: "admin", label: "Admin", description: "Full access to all features", level: 7 },
  { value: "customer_success", label: "Customer Success", description: "Manage contacts and support", level: 6 },
  { value: "staff", label: "Staff", description: "Can manage contacts, deals, bookings", level: 6 },
  { value: "teacher", label: "Teacher", description: "Can manage lessons and students", level: 5 },
  { value: "affiliate", label: "Affiliate", description: "Can view referral dashboard", level: 4 },
] as const;

type TeamMember = OrganizationMemberRecord;
type Invitation = OrganizationInvitationRecord;

export default function TeamPage() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("staff");
  const [isInviting, setIsInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchTeam = useCallback(async () => {
    try {
      const [membersRes, invitesRes] = await Promise.all([
        listOrganizationMembers(),
        listOrganizationInvitations().catch(() => [] as Invitation[]),
      ]);
      setMembers(membersRes);
      setInvitations(invitesRes);
    } catch {
      setMembers([]);
      setInvitations([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setIsInviting(true);
    setError(null);
    setSuccess(null);
    try {
      await inviteOrganizationMember({ email: inviteEmail.trim(), role: inviteRole });
      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      await fetchTeam();
    } catch {
      setError("Failed to send invitation. Check that your email settings are configured.");
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    const confirmed = window.confirm(`Remove ${memberName} from this team? They will lose access immediately.`);
    if (!confirmed) return;
    try {
      await removeOrganizationMember(memberId);
      setSuccess(`${memberName} has been removed`);
      await fetchTeam();
    } catch {
      setError("Failed to remove team member");
    }
  };

  const handleCancelInvite = async (inviteId: string) => {
    try {
      await cancelOrganizationInvitation(inviteId);
      await fetchTeam();
    } catch {
      setError("Failed to cancel invitation");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl space-y-6">
        <Skeleton className="h-8 w-48" />
        {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-16" />)}
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-8">
      {/* Invite */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-[var(--color-primary)]" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Invite team member</h2>
        </div>

        {error && (
          <div className="mb-3 rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-2 text-sm text-[var(--color-error)]">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-3 rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 px-4 py-2 text-sm text-[var(--color-success)]">
            {success}
          </div>
        )}

        <form onSubmit={handleInvite} className="flex gap-3">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-muted-foreground)]" />
            <input
              type="email"
              autoComplete="email"
              placeholder="colleague@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] pl-9 pr-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
            />
          </div>
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            {ROLES.map((role) => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={isInviting}
            className="rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {isInviting ? "Sending..." : "Invite"}
          </button>
        </form>
      </section>

      {/* Pending invitations */}
      {invitations.filter((inv) => inv.status === "pending").length > 0 && (
        <section>
          <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-3">Pending Invitations</h2>
          <div className="rounded-xl border border-[var(--color-border)] divide-y divide-[var(--color-border)]">
            {invitations
              .filter((inv) => inv.status === "pending")
              .map((inv) => (
                <div key={inv.id} className="stagger-item flex items-center justify-between p-4">
                  <div>
                    <p className="text-sm font-medium text-[var(--color-foreground)]">{inv.email}</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">
                      Expires {formatDate(inv.expiresAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="rounded-full bg-[var(--color-warning)]/10 px-2.5 py-0.5 text-xs font-medium text-[var(--color-warning)]">
                      {inv.role}
                    </span>
                    <button
                      onClick={() => handleCancelInvite(inv.id)}
                      className="text-[var(--color-muted-foreground)] hover:text-[var(--color-error)]"
                      title="Cancel invitation"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* Team members */}
      <section>
        <h2 className="text-lg font-semibold text-[var(--color-foreground)] mb-3">Team members</h2>
        <p className="text-sm text-[var(--color-muted-foreground)] mb-4">Manage who has access to your account</p>
        <div className="rounded-xl border border-[var(--color-border)] divide-y divide-[var(--color-border)]">
          {members.length === 0 ? (
            <div className="p-8 text-center text-sm text-[var(--color-muted-foreground)]">
              No team members yet. Invite someone to get started.
            </div>
          ) : (
            members.map((member) => (
              <div key={member.id} className="stagger-item flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  {member.image ? (
                    <img src={member.image} alt="" className="h-9 w-9 rounded-full" />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-primary)]/10 text-sm font-medium text-[var(--color-primary)]">
                      {(member.name ?? member.email).charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium text-[var(--color-foreground)]">
                      {member.name ?? member.email}
                    </p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-[var(--color-primary)]/10 px-2.5 py-0.5 text-xs font-medium text-[var(--color-primary)]">
                    {member.role}
                  </span>
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    {formatDate(member.joinedAt)}
                  </p>
                  {member.role !== "owner" && (
                    <button
                      onClick={() => handleRemoveMember(member.id, member.name ?? member.email)}
                      className="text-[var(--color-muted-foreground)] hover:text-[var(--color-error)]"
                      title="Remove member"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Roles reference */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Shield className="h-4 w-4 text-[var(--color-muted-foreground)]" />
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Roles</h2>
        </div>
        <div className="space-y-2">
          {ROLES.map((role) => (
            <div key={role.value} className="stagger-item flex items-center justify-between rounded-lg border border-[var(--color-border)] p-3">
              <div>
                <p className="text-sm font-medium text-[var(--color-foreground)]">{role.label}</p>
                <p className="text-xs text-[var(--color-muted-foreground)]">{role.description}</p>
              </div>
              <span className="text-xs text-[var(--color-muted-foreground)]">Level {role.level}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
